import { z } from 'zod';
import { API_KEY_GRANTABLE_PERMISSIONS, BROWSER_EXTENSION_ONLY_PERMISSIONS } from '../permissions';
import { AdminGrantablePermissionSchema } from '../admin/api-key.schemas';
import { refersTo } from '../common/refers-to';

const WorkspaceIdPathParamsSchema = z.object({ workspaceId: z.uuid() });

/**
 * The rolling window an API key's `rateLimit` is counted over: `rateLimit: 50`
 * admits 50 calls per this window. Canonical — the backend limiter imports it
 * instead of declaring its own, and the surfaces that name the unit in copy are
 * tested against it, so the window and the wording cannot drift apart.
 */
export const API_KEY_RATE_LIMIT_WINDOW_MS = 1_000;

/**
 * The daily volume cap stamped onto every MCP-capable key at issuance
 * (IMM-601.1). Platform-set, not member-configurable this delivery; stored per
 * key so it can be tuned centrally later without reinterpreting old rows.
 * Enforcement lands with the shared usage recorder (IMM-601.2) — here the value
 * is stored and displayed only.
 */
export const DAILY_CALL_LIMIT_DEFAULT = 2_000;

/**
 * The burst allowance an MCP-capable credential gets when nobody picks one:
 * requests per `API_KEY_RATE_LIMIT_WINDOW_MS`. Named because a second credential
 * type now seeds it — an OAuth grant (IMM-601.8.2) has no issuance form to
 * choose it on, and it must not drift from what an MCP-capable key gets.
 */
export const API_KEY_RATE_LIMIT_DEFAULT = 50;

/**
 * The only expiries an MCP-capable key may carry (IMM-601.1) — no free-form
 * date. The default is a UI preselection: the contract never defaults
 * `expiresInDays`, because an MCP issuance without an explicit expiry is a
 * domain refusal (`identity.api-key.expiry-required`), not a silent 90.
 */
export const MCP_KEY_EXPIRY_DAYS = [30, 90, 180] as const;
export const MCP_KEY_EXPIRY_DEFAULT_DAYS = 90;

// `expired` is DERIVED from `expiresAt` at read time (IMM-601.1) — the stored
// status column knows only active/revoked, and revoked wins over expired.
export const ApiKeyStatusSchema = z.enum(['active', 'revoked', 'expired']);

export type ApiKeyStatus = z.infer<typeof ApiKeyStatusSchema>;

/**
 * The permissions a caller may attach to a key, restricted to the
 * grantable allow-list at the type level — a payload naming any other
 * permission is rejected at the boundary (400) before the domain ever
 * runs the issuer-holds check.
 */
export const GrantablePermissionSchema = z.enum(API_KEY_GRANTABLE_PERMISSIONS);

export const ApiKeyListItemSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string().nullable(),
  status: ApiKeyStatusSchema,
  rateLimit: z.number().int(),
  grantedPermissions: z.array(z.string()),
  memberId: refersTo('member', z.uuid()),
  createdAt: z.iso.datetime(),
  lastUsedAt: z.iso.datetime().nullable(),
  revokedAt: z.iso.datetime().nullable(),
  // MCP-capable credential fields (IMM-601.1). `expiresAt` / `dailyCallLimit`
  // are non-null exactly when `mcpCapable` — the DB CHECK enforces the
  // mcp→non-null direction, the domain (sole minting path) the converse — and
  // a plain HTTP key carries false/null/null.
  mcpCapable: z.boolean(),
  expiresAt: z.iso.datetime().nullable(),
  dailyCallLimit: z.number().int().nullable(),
  /**
   * The Lead-Partner this key was issued for (IMM-829.1), or `null` for every
   * ordinary key — which is most of them.
   *
   * A SECOND fact, never a fourth `status`. Both are true at once: the
   * credential is `active` (it authenticates, it is not revoked) while the
   * integration behind it is dead, because lead creation refuses a
   * `PARTNER`-sourced lead whose partner is inactive. Collapsing them into the
   * status enum would make the row lie in the other direction, so
   * `ApiKeyStatusSchema` stays `active | revoked | expired` and the partner's
   * state travels beside it.
   */
  leadPartner: z.object({ id: z.uuid(), name: z.string(), active: z.boolean() }).nullable(),
});

export type ApiKeyListItem = z.infer<typeof ApiKeyListItemSchema>;

export const ListApiKeysPathParamsSchema = WorkspaceIdPathParamsSchema;

/**
 * Which keys to list (IMM-895): `mcpCapable=false` = HTTP-API keys only,
 * `mcpCapable=true` = MCP keys only — the same flag every list item carries.
 * The two live on separate settings pages, and the split is the API's to
 * enforce, not each screen's to remember.
 *
 * Omitted = both, newest-first, exactly as before the parameter existed — so an
 * integration already calling this endpoint is not broken by it. `mcpCapable`
 * is fixed at issuance, so a key never moves between the two.
 *
 * Deliberately NOT `kind`: that word already means workspace-vs-admin key
 * (`ApiKeyKindSchema`, and the operator list's own `kind` filter).
 */
export const ListApiKeysQuerySchema = z.object({
  mcpCapable: z.stringbool().optional(),
});

export const ListApiKeysResponseSchema = z.object({
  data: z.array(ApiKeyListItemSchema),
});

export type ListApiKeysResponse = z.infer<typeof ListApiKeysResponseSchema>;

export const IssueApiKeyPathParamsSchema = WorkspaceIdPathParamsSchema;

export const IssueApiKeyBodySchema = z
  .object({
    name: z.string().trim().min(1).max(80),
    description: z.string().trim().max(500).optional(),
    // Requests per `API_KEY_RATE_LIMIT_WINDOW_MS`.
    rateLimit: z.number().int().min(1).max(60).default(API_KEY_RATE_LIMIT_DEFAULT),
    // The boundary accepts the extension-only literals; the domain decides whether they are grantable.
    grantedPermissions: z
      .array(z.union([GrantablePermissionSchema, z.enum(BROWSER_EXTENSION_ONLY_PERMISSIONS)]))
      .min(1),
    // Marks the key MCP-fähig (IMM-601.1): the verifier injects
    // `workspace:identity:mcp-invoke` and the key gains a mandatory expiry and
    // the platform daily call cap. Default false = today's plain HTTP key.
    mcpCapable: z.boolean().optional(),
    // Only the three bounded options, never a free-form date. Mandatory for an
    // MCP issuance — but enforced in the DOMAIN (422 `identity.api-key.expiry-required`)
    // so the invariant holds for every caller, not only the HTTP one.
    expiresInDays: z.union([z.literal(30), z.literal(90), z.literal(180)]).optional(),
  })
  // Expiry is an MCP-key concept only (AC 17: non-MCP keys are byte-for-byte
  // unchanged), so an expiry on a plain key is a malformed request, not a
  // business rule — rejected at the boundary before the domain runs.
  .refine((body) => body.expiresInDays === undefined || body.mcpCapable === true, {
    message: 'expiresInDays is only allowed when mcpCapable is true',
    path: ['expiresInDays'],
  });

export type IssueApiKeyBody = z.infer<typeof IssueApiKeyBodySchema>;

// The only surface that ever carries the plaintext `secret` — rendered
// once at issuance, never persisted in retrievable form, never returned
// by the list endpoint.
export const IssueApiKeyResponseSchema = ApiKeyListItemSchema.extend({
  secret: z.string(),
});

export type IssueApiKeyResponse = z.infer<typeof IssueApiKeyResponseSchema>;

export const RevokeApiKeyPathParamsSchema = WorkspaceIdPathParamsSchema.extend({
  apiKeyId: z.uuid(),
});

// Revocation is idempotent — revoking an already-revoked key returns the
// same metadata with `status: 'revoked'` and the original revocation
// timestamp, never the secret.
export const RevokeApiKeyResponseSchema = ApiKeyListItemSchema;

export type RevokeApiKeyResponse = z.infer<typeof RevokeApiKeyResponseSchema>;

export const UpdateApiKeyPathParamsSchema = WorkspaceIdPathParamsSchema.extend({
  apiKeyId: z.uuid(),
});

export const UpdateApiKeyBodySchema = z.object({
  // Requests per `API_KEY_RATE_LIMIT_WINDOW_MS`. No default: a default on a
  // full replace silently rewrites a field the caller did not mention.
  rateLimit: z.number().int().min(1).max(60),
  // The ADMIN catalogue, not the customer one: a retained operator-granted
  // grant must pass the boundary, or a full replace could only delete it
  // (BL-137). The customer catalogue is enforced on ADDED permissions in
  // `ApiKey.updateByMember`.
  grantedPermissions: z.array(AdminGrantablePermissionSchema).min(1),
});

export type UpdateApiKeyBody = z.infer<typeof UpdateApiKeyBodySchema>;

export const UpdateApiKeyResponseSchema = ApiKeyListItemSchema;

export type UpdateApiKeyResponse = z.infer<typeof UpdateApiKeyResponseSchema>;
